import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Inject,
    Injector,
    Input,
    Output,
} from '@angular/core';
import { BaseControlComponent } from '../base-control/base-control.component';
import {
    DatePickerType,
    DatePickerTypePropMap,
    DatePickerTypeProps,
} from './date-picker.types';
import { PresetRanges } from 'ng-zorro-antd/date-picker/standard-types';
import { DATE_CONFIG } from '@pf/shared-services';
import { IDateConfig } from '@pf/shared-common';
import { wait } from '@pf/shared-utility';
import { NZ_I18N, NzI18nInterface } from 'ng-zorro-antd/i18n';

@Component({
    selector: 'pf-date-picker',
    templateUrl: './date-picker.component.html',
    styleUrls: ['./date-picker.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DatePickerComponent extends BaseControlComponent {
    private _datePickerType!: DatePickerType;

    /**
     * The `placeholder` input is used as a placeholder on the input
     * component.
     */
    @Input() placeholder = '';

    @Input() presetRanges?: PresetRanges;
    timeFormat: string;

    /**
     * The `datePickerType` input is allowing us to specify the `pf-input` type.
     * [DatePickerType]{@link DatePickerType}
     */
    @Input()
    set datePickerType(value: keyof typeof DatePickerType) {
        this._datePickerType = DatePickerType[value];
        this.setProps();
    }

    @Input() showTime? = false;

    @Output() calendarChanged = new EventEmitter<Date[]>();

    /**
     * This property will be used to set different input components' property values
     */
    typeProps?: DatePickerTypeProps;

    get dateFormat() {
        return this.showTime
            ? this.dateConfig.dateFormat + ' ' + this.dateConfig.timeFormat
            : this.dateConfig.dateFormat;
    }

    constructor(
        injector: Injector,
        elementRef: ElementRef,
        @Inject(DATE_CONFIG) private dateConfig: IDateConfig,
        @Inject(NZ_I18N) private nzI18n: NzI18nInterface,
        private cdr: ChangeDetectorRef
    ) {
        super(injector, elementRef);
        this.timeFormat = dateConfig.timeFormat;
    }

    /**
     * The start time or the end time of the range change callback
     * @param $event
     */
    calendarChangedHandler($event: (Date | null)[]) {
        if ($event !== null) {
            this.calendarChanged.emit($event as Date[]);
        }
    }

    /**
     * This function sets the [`typeProps`]{@link InputComponent#typeProps} fields regarding
     * the given [`inputType`]{@link inputType}.
     */
    private async setProps() {
        await wait(0);
        this.typeProps = DatePickerTypePropMap[this._datePickerType]();
        this.cdr.markForCheck();
        if (this.formControl) {
            this.formControlConfig(this.formControl);
        }
    }
}
